/**
 * Quiz "Monte sua rotina" (sections/quiz.liquid).
 * Lê perguntas, regras e textos do JSON da seção e monta cada passo: perguntas (uma opção por
 * toque, avança sozinho), passo de dados opcional (nome, e-mail, WhatsApp) e o resultado com a
 * rotina, o total e o botão que coloca tudo na sacola de verdade (/cart/add.js).
 *
 * Regras: cada uma entra quando TODAS as condições batem. Condição = "N=Opção A,Opção B" (a
 * resposta da pergunta N é uma delas) ou "N!=Opção" (não é nenhuma delas). Comparação sem
 * acentos e sem diferenciar maiúsculas.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const norm = (text) =>
    String(text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  const esc = (text) =>
    String(text == null ? '' : text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const icon = (name) => {
    const paths = {
      left: '<path d="M15 6l-6 6 6 6"/>',
      check: '<path d="m5 12 4.5 4.5L19 7"/>',
    };
    return `<svg class="bz-i" viewBox="0 0 24 24" aria-hidden="true">${paths[name]}</svg>`;
  };

  const init = (section) => {
    const shell = section.querySelector('[data-bz-quiz]');
    const configEl = section.querySelector('[data-q-config]');
    if (!shell || !configEl || shell.dataset.ready) return;
    shell.dataset.ready = 'true';

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }

    const Q = config.questions || [];
    if (!Q.length) return;
    const T = config.text;
    const L = config.lead;
    const main = shell.querySelector('[data-q-main]');
    const prog = shell.querySelector('[data-q-prog]');
    const bg = shell.querySelector('[data-q-bg]');
    const money = new Intl.NumberFormat(config.locale || 'pt-BR', { style: 'currency', currency: config.currency || 'BRL' });
    const brl = (cents) => money.format(cents / 100);

    const steps = Q.map((q) => q.title);
    if (L.enabled) steps.push(L.step);

    let qi = 0;
    let answers = {};
    let lead = null;
    // Medição (window.bzTrack, assets/lumina.js): início e conclusão contam uma vez por rodada
    let started = false;
    let finished = false;

    const label = (i) => {
      const option = Q[i] && Q[i].options.find((o) => o.label === answers[i]);
      return option ? option.label : '';
    };

    // Regras -> produtos da rotina, ordenados pela posição, sem repetir produto
    const matches = (when) => {
      if (!when) return true;
      return when
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean)
        .every((part) => {
          const negate = part.includes('!=');
          const [left, right = ''] = part.split(negate ? '!=' : '=');
          let index = parseInt(left, 10) - 1;
          if (Number.isNaN(index)) index = Q.findIndex((q) => norm(q.title) === norm(left));
          const answer = norm(answers[index]);
          const wanted = right.split(',').map(norm).filter(Boolean);
          const hit = wanted.includes(answer);
          return negate ? !hit : hit;
        });
    };

    const recommend = () => {
      const seen = new Set();
      return (config.rules || [])
        .map((rule, order) => ({ ...rule, order }))
        .filter((rule) => matches(rule.when))
        .sort((a, b) => a.position - b.position || a.order - b.order)
        .filter((rule) => (seen.has(rule.product.id) ? false : seen.add(rule.product.id)));
    };

    const setBackground = () => {
      if (!bg) return;
      const step = Q[Math.min(qi, Q.length - 1)];
      const src = (step && step.image) || config.defaultImage;
      if (src) {
        bg.hidden = false;
        if (bg.getAttribute('src') !== src) bg.src = src;
      }
    };

    const renderProgress = () => {
      const current = qi < Q.length ? qi : lead || !L.enabled ? steps.length : Q.length;
      prog.innerHTML = steps
        .map((title, i) => {
          const state = i < current ? 'done' : i === current ? 'cur' : '';
          const answer = i < Q.length && answers[i] ? `<small>${esc(label(i))}</small>` : '';
          return `<li class="${state}"><i>${i < current ? '✓' : i + 1}</i><span>${esc(title)}${answer}</span></li>`;
        })
        .join('');
    };

    const top = (width, text) =>
      `<div class="top"><div class="bz-qbar"><i style="width:${width}%"></i></div><span class="bz-small">${esc(text)}</span></div>`;
    const counter = (n) => T.counter.replace('[n]', n).replace('[total]', steps.length);

    const renderQuestion = () => {
      const step = Q[qi];
      const two = step.options.length === 2 || step.options.length === 4;
      main.innerHTML = `${top((qi / steps.length) * 100, counter(qi + 1))}
        <h3 class="q">${step.question}</h3>
        ${step.hint ? `<p class="hint">${esc(step.hint)}</p>` : ''}
        <div class="bz-qopts${two ? ' two' : ''}">
          ${step.options
            .map(
              (o, i) => `<button type="button" class="bz-qo" aria-pressed="${answers[qi] === o.label}" data-a="${esc(o.label)}" style="animation-delay:${i * 40}ms">
                <span class="dot2"></span><span><b>${esc(o.label)}</b>${o.sub ? `<small>${esc(o.sub)}</small>` : ''}</span></button>`
            )
            .join('')}
        </div>
        <div class="bz-qnav"><button type="button" class="bz-qback" data-q-back ${qi ? '' : 'disabled'}>${icon('left')}${esc(T.back)}</button><span class="bz-small">${esc(T.tap)}</span></div>`;

      main.querySelectorAll('.bz-qo').forEach((button) => {
        button.addEventListener('click', () => {
          if (qi === 0 && !started) {
            started = true;
            if (window.bzTrack) window.bzTrack('quiz_iniciado', { resposta: button.dataset.a });
          }
          answers[qi] = button.dataset.a;
          main.querySelectorAll('.bz-qo').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
          setTimeout(() => {
            qi += 1;
            render();
          }, reduce ? 0 : 220);
        });
      });
      main.querySelector('[data-q-back]').addEventListener('click', () => {
        qi = Math.max(0, qi - 1);
        render();
      });
    };

    const sendLead = (data) => {
      if (L.mode === 'none') return;
      const body = new FormData();
      body.append('utf8', '✓');
      const answerTags = Object.keys(answers)
        .sort()
        .map((i) => `quiz-${norm(answers[i]).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`);
      if (L.mode === 'customer') {
        body.append('form_type', 'customer');
        body.append('contact[email]', data.email);
        body.append('contact[first_name]', data.name);
        body.append('contact[phone]', data.phone);
        body.append('contact[accepts_marketing]', 'true');
        body.append('contact[tags]', [L.tags, ...answerTags].filter(Boolean).join(','));
      } else {
        body.append('form_type', 'contact');
        body.append('contact[email]', data.email);
        body.append('contact[name]', data.name);
        body.append('contact[phone]', data.phone);
        body.append('contact[body]', Q.map((q, i) => `${q.title}: ${answers[i] || '-'}`).join('\n'));
      }
      fetch(`${root}contact`, { method: 'POST', body, headers: { Accept: 'text/html' } }).catch(() => {});
    };

    const renderLead = () => {
      main.innerHTML = `<div class="bz-qlead">${top((Q.length / steps.length) * 100, counter(steps.length))}
        <h3 class="q">${L.title}</h3>
        ${L.hint ? `<p class="hint">${esc(L.hint)}</p>` : ''}
        <form class="bz-qlead-form" novalidate>
          <label class="bz-sr" for="qlName-${section.id}">${esc(L.name)}</label><input id="qlName-${section.id}" name="name" placeholder="${esc(L.name)}" autocomplete="name" required>
          <label class="bz-sr" for="qlEmail-${section.id}">${esc(L.email)}</label><input id="qlEmail-${section.id}" name="email" type="email" placeholder="${esc(L.email)}" autocomplete="email" required>
          <label class="bz-sr" for="qlPhone-${section.id}">${esc(L.phone)}</label><input id="qlPhone-${section.id}" name="phone" type="tel" placeholder="${esc(L.phone)}" autocomplete="tel" required>
          <p class="bz-qlead-msg" role="status"></p>
          <div class="bz-qnav"><button class="bz-qback" type="button" data-q-back>${icon('left')}${esc(T.back)}</button><button class="bz-btn" type="submit">${esc(L.submit)}</button></div>
        </form>
        ${L.fine ? `<p class="fine">${L.fine}</p>` : ''}</div>`;

      const form = main.querySelector('form');
      main.querySelector('[data-q-back]').addEventListener('click', () => {
        qi = Q.length - 1;
        render();
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const name = form.name.value.trim();
        const email = form.email.value.trim();
        const phone = form.phone.value.trim();
        const msg = form.querySelector('.bz-qlead-msg');
        if (!name) {
          msg.textContent = L.errName;
          form.name.focus();
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          msg.textContent = L.errEmail;
          form.email.focus();
          return;
        }
        if (phone.replace(/\D/g, '').length < 10) {
          msg.textContent = L.errPhone;
          form.phone.focus();
          return;
        }
        lead = { name, email, phone };
        sendLead(lead);
        render();
      });
    };

    const renderResult = () => {
      const rules = recommend();
      const items = rules.map((rule) => rule.product);
      const known = items.length > 0 && items.every((p) => p.price > 0);
      const total = items.reduce((sum, p) => sum + (p.price || 0), 0);
      const objective = (Q[0] && Q[0].options.find((o) => o.label === answers[0])) || null;
      const objectiveText = objective ? objective.label.toLowerCase() : '';
      const first = lead && lead.name ? lead.name.trim().split(' ')[0] : '';
      const titleTemplate = first ? T.titleNamed : T.title;
      const title = esc(titleTemplate)
        .replace('[nome]', esc(first))
        .replace(/\[objetivo\]([.!]?)/, `<span>${esc(objectiveText)}$1</span>`);
      if (!finished) {
        finished = true;
        if (window.bzTrack) window.bzTrack('quiz_concluido', { objetivo: answers[0] || '', produtos: items.length, lead: Boolean(lead) });
      }
      const hint = T.hint.replace(/\[r(\d)\]/g, (m, n) => (answers[n - 1] || '').toLowerCase());

      let freight = T.free;
      if (config.freeShipping > 0 && total < config.freeShipping) freight = T.missing.replace('[valor]', brl(config.freeShipping - total));
      if (!config.freeShipping) freight = '';
      const parcel = Math.floor(total / config.installments);
      const pix = Math.round((total * (100 - config.pix)) / 100);
      let line = T.priceLine.replace('[n]', config.installments).replace('[parcela]', brl(parcel)).replace('[pix]', brl(pix)).replace('[frete]', freight);
      if (!config.pix) line = line.replace(/\s*·[^·]*no Pix/, '');
      line = line.replace(/\s*·\s*$/, '');

      const listHtml = items
        .map(
          (p, i) => `<div class="it" style="animation-delay:${i * 60}ms"><figure>${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : ''}</figure>
            <div><span class="r">${i + 1} · ${esc(rules[i].step)}</span><b>${esc(p.title)}</b></div>
            <span class="p">${p.price > 0 ? brl(p.price) : `<span class="bz-tag-ph">${esc(T.pricePlaceholder)}</span>`}</span></div>`
        )
        .join('');

      const seeLink = objective && objective.collection ? `${root}collections/${encodeURIComponent(objective.collection)}` : `${root}collections/all`;
      main.innerHTML = `<div class="bz-qres">${top(100, T.resultLabel)}
        <h3 class="q">${title}</h3><p class="hint">${esc(hint)}</p>
        <div class="list">${listHtml}</div>
        <div class="foot"><div><small>${esc(T.total.replace('[n]', items.length))}</small>
          <b>${known ? brl(total) : `R$ <span class="bz-tag-ph">${esc(T.pricePlaceholder)}</span>`}</b>
          ${known ? `<small>${esc(line)}</small>` : ''}</div>
          <div class="acts"><button type="button" class="bz-btn bz-btn-line" data-q-redo>${esc(T.redo)}</button>
          ${known ? `<button type="button" class="bz-btn" data-q-add>${esc(T.add)}</button>` : `<a class="bz-btn" href="${seeLink}"${objective && objective.collection ? ` data-bz-need="${esc(objective.collection)}" data-bz-need-label="${esc(objective.label)}"` : ''}>${esc(T.seeProducts)}</a>`}</div></div></div>`;

      main.querySelector('[data-q-redo]').addEventListener('click', () => {
        qi = 0;
        answers = {};
        lead = null;
        started = false;
        finished = false;
        render();
      });

      const add = main.querySelector('[data-q-add]');
      if (add) {
        add.addEventListener('click', async () => {
          const payload = items.filter((p) => p.available).map((p) => ({ id: p.variant, quantity: 1, properties: { _origem: 'quiz' } }));
          if (!payload.length) return;
          add.disabled = true;
          try {
            const response = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: payload }),
            });
            if (!response.ok) throw new Error(String(response.status));
            if (window.bzTrack) window.bzTrack('quiz_adicionou', { itens: payload.length, valor: total / 100, objetivo: answers[0] || '' });
            if (window.themeCart) await window.themeCart.refresh();
            add.innerHTML = `${icon('check')}${esc(T.added)}`;
            if (window.lumina) window.lumina.toast(T.toast);
          } catch (error) {
            add.disabled = false;
          }
        });
      }
    };

    const render = () => {
      setBackground();
      renderProgress();
      if (qi < Q.length) renderQuestion();
      else if (L.enabled && !lead) renderLead();
      else renderResult();
    };

    render();
  };

  const boot = (scope = document) => scope.querySelectorAll('.bz-quiz-sec').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
